import React from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from "react-redux";
import { selectHelpFab } from "../../features/layout/helpFabSlice";
import { selectIsPremium } from '../../features/account/accountSlice';
import { selectThemeSettings } from '../../features/app/appSlice';

function normalizeHex(input) {
    if (!input) return null
    const s = String(input).trim()
    if (!s) return null

    if (s.startsWith('#')) {
        if (s.length === 4) return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`
        if (s.length === 7) return s
        return null
    }

    if (/^[0-9a-fA-F]{6}$/.test(s)) return `#${s}`
    return null
}
function rgba(hex, a) {
    const h = normalizeHex(hex)
    if (!h) return `rgba(124, 58, 237, ${a})`
    const r = parseInt(h.slice(1, 3), 16)
    const g = parseInt(h.slice(3, 5), 16)
    const b = parseInt(h.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${a})`
}

export default function FreemiumUpgradeBox({ t, account }) {
    const isPremium = useSelector(selectIsPremium);
    const { phone } =
        useSelector(selectHelpFab);
    const { secondaryColor: secondary } = useSelector(selectThemeSettings)

    const whatsappText = `Hi Trucarbon, I want to upgrade to Premium.`
    const waHref = `https://wa.me/${phone}?text=${encodeURIComponent(whatsappText)}`


    if (isPremium) return null


    const styles = {
        wrap: {
            padding: '12px 16px 16px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            background: 'transparent',
            boxSizing: 'border-box',
        },

        card: {
            borderRadius: 14,
            padding: 12,
            background: `linear-gradient(135deg,
        ${rgba(secondary, 0.26)},
        rgba(255,255,255,0.06)
      )`,
            border: `1px solid ${rgba(secondary, 0.55)}`,
            boxShadow: `0 10px 28px rgba(0,0,0,0.30),
                  0 0 0 1px rgba(255,255,255,0.06),
                  0 0 22px ${rgba(secondary, 0.35)}`,
            color: 'rgba(255,255,255,0.94)',
            boxSizing: 'border-box',
            position: 'relative',
            overflow: 'hidden',
        },

        sheen: {
            position: 'absolute',
            inset: -60,
            background:
                'radial-gradient(circle at 20% 10%, rgba(255,255,255,0.18), transparent 55%)',
            pointerEvents: 'none',
        },

        badge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            fontWeight: 800,
            padding: '4px 8px',
            borderRadius: 999,
            background: rgba(secondary, 0.22),
            border: `1px solid ${rgba(secondary, 0.55)}`,
            marginBottom: 8,
        },

        title: {
            fontSize: 14,
            fontWeight: 900,
            marginBottom: 6,
            lineHeight: 1.2,
        },

        desc: {
            fontSize: 12,
            lineHeight: 1.4,
            marginBottom: 10,
            color: 'rgba(255,255,255,0.86)',
        },

        list: {
            margin: '0 0 12px 18px',
            padding: 0,
            fontSize: 12,
            color: 'rgba(255,255,255,0.88)',
        },

        listItem: { marginBottom: 6 },

        cta: {
            display: 'flex',
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 8,
            textDecoration: 'none',
            fontWeight: 900,
            fontSize: 12,
            padding: '10px 12px',
            borderRadius: 12,
            background: `linear-gradient(135deg,
        ${rgba(secondary, 0.95)},
        ${rgba(secondary, 0.70)}
      )`,
            border: '1px solid rgba(255,255,255,0.22)',
            color: 'rgba(255,255,255,0.96)',
            boxSizing: 'border-box',
            boxShadow: `0 10px 18px ${rgba(secondary, 0.25)}`,
        },

        ctaHover: {
            filter: 'brightness(1.06)',
            transform: 'translateY(-1px)',
        },

        footnote: {
            marginTop: 8,
            fontSize: 11,
            textAlign: 'center',
            color: 'rgba(255,255,255,0.78)',
        },
    }
    return (
        <div style={styles.wrap}>
            <div style={styles.card}>
                <div style={styles.badge}>
                    <i className="ri-vip-crown-2-line" />
                    {t?.('menu.upgrade.badge') || 'Premium'}
                </div>

                <div style={styles.title}>
                    {t?.('menu.upgrade.title') || 'Unlock more features'}
                </div>

                <div style={styles.desc}>
                    {t?.('menu.upgrade.desc') ||
                        'Upgrade to access advanced reports, analytics, and priority support.'}
                </div>

                <ul style={styles.list}>
                    <li style={styles.listItem}>
                        {t?.('menu.upgrade.point1')}
                    </li>
                    <li style={styles.listItem}>
                        {t?.('menu.upgrade.point2')}
                    </li>
                    <li style={styles.listItem}>
                        {t?.('menu.upgrade.point3')}
                    </li>
                </ul>

                <HoverLink
                    to={waHref}
                    style={styles.cta}
                    hoverStyle={styles.ctaHover}
                >
                    {t?.('menu.upgrade.cta') || 'Upgrade to Premium'}
                    <i className="ri-arrow-right-line" />
                </HoverLink>

                <div style={styles.footnote}>
                    {t?.('menu.upgrade.footnote') || 'Takes less than 2 minutes.'}
                </div>
            </div>
        </div>
    )
}

function HoverLink({ to, style, hoverStyle, children }) {
    const [hover, setHover] = React.useState(false)

    return (
        <Link
            to={to}
            target='_blank'
            style={{ ...style, ...(hover ? hoverStyle : {}) }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            {children}
        </Link>
    )
}
